import { useEffect, useState, useCallback } from "react";
import { Loader2, Sparkles, Plus, Check, X, Pencil, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail, OFFER_UNITS, brandOfferText, brandOfferValidityText } from "@/lib/api";

const EMPTY = {
  brand: "",
  offer_mode: "both",       // "cash" | "free" | "both"
  offer_buy_qty: "1",
  offer_buy_unit: "Case",
  offer_cash_discount: "",
  offer_free_qty: "",
  offer_free_unit: "Piece",
  offer_free_item: "",
  start_date: "",
  end_date: "",
  description: "",
};

export default function AdminBrandOffers() {
  const [brands, setBrands] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/brands"), api.get("/brand-offers")])
      .then(([b, o]) => { setBrands(b.data); setOffers(o.data); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => {
    setError("");
    setEditingId(null);
    setForm({ ...EMPTY, brand: brands[0]?.name || "" });
    setShowForm(true);
  };

  const openEdit = (o) => {
    setError("");
    setEditingId(o.id);
    const hasCash = Number(o.offer_cash_discount) > 0;
    const hasFree = Number(o.offer_free_qty) > 0;
    setForm({
      brand: o.brand,
      offer_mode: o.offer_mode || (hasCash && hasFree ? "both" : hasFree ? "free" : "cash"),
      offer_buy_qty: o.offer_buy_qty ?? "1",
      offer_buy_unit: o.offer_buy_unit || "Case",
      offer_cash_discount: o.offer_cash_discount ?? "",
      offer_free_qty: o.offer_free_qty ?? "",
      offer_free_unit: o.offer_free_unit || "Piece",
      offer_free_item: o.offer_free_item ?? "",
      start_date: o.start_date ?? "",
      end_date: o.end_date ?? "",
      description: o.description ?? "",
    });
    setShowForm(true);
  };

  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const save = async (e) => {
    e.preventDefault();
    setError("");
    const useCash = form.offer_mode === "cash" || form.offer_mode === "both";
    const useFree = form.offer_mode === "free" || form.offer_mode === "both";
    if (useCash && !form.offer_cash_discount && !useFree) {
      setError("Enter a cash discount amount."); return;
    }
    if (useFree && !form.offer_free_item) {
      setError("Enter the free item name."); return;
    }
    setSaving(true);
    const payload = {
      brand: form.brand,
      offer_type: form.offer_mode,
      offer_mode: form.offer_mode,
      offer_buy_qty: num(form.offer_buy_qty) || 1,
      offer_buy_unit: form.offer_buy_unit || null,
      offer_cash_discount: useCash ? num(form.offer_cash_discount) : null,
      offer_free_qty: useFree ? num(form.offer_free_qty) : null,
      offer_free_unit: useFree ? (form.offer_free_unit || null) : null,
      offer_free_item: useFree ? (form.offer_free_item || null) : null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      description: form.description || null,
    };
    try {
      if (editingId) await api.put(`/brand-offers/${editingId}`, payload);
      else await api.post("/brand-offers", payload);
      setShowForm(false);
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this brand offer? Each product's original price will be restored.")) return;
    await api.delete(`/brand-offers/${id}`);
    load();
  };

  const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";
  const labelCls = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500";
  const useCash = form.offer_mode === "cash" || form.offer_mode === "both";
  const useFree = form.offer_mode === "free" || form.offer_mode === "both";

  return (
    <div data-testid="admin-brand-offers">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500">{offers.length} brand offer(s). Applied to all products of the brand.</p>
        <button data-testid="new-brand-offer-button" onClick={openNew} className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700">
          <Plus className="h-4 w-4" /> New Brand Offer
        </button>
      </div>

      {error && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

      {showForm && (
        <form onSubmit={save} data-testid="brand-offer-form" className="mb-5 space-y-3 rounded-xl border-2 border-indigo-100 bg-indigo-50/40 p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Brand</label>
              <select data-testid="brand-offer-brand" required value={form.brand} onChange={(e) => set("brand", e.target.value)} disabled={!!editingId} className={`${inputCls} ${editingId ? "bg-slate-100" : ""}`}>
                <option value="" disabled>Select brand</option>
                {brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Offer Type</label>
              <select data-testid="brand-offer-mode" value={form.offer_mode} onChange={(e) => set("offer_mode", e.target.value)} className={inputCls}>
                <option value="cash">Cash Discount only</option>
                <option value="free">Free Item only</option>
                <option value="both">Cash Discount + Free Item</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <label className={labelCls}>Buy Qty</label>
              <input data-testid="brand-offer-buy-qty" type="number" inputMode="numeric" value={form.offer_buy_qty} onChange={(e) => set("offer_buy_qty", e.target.value)} placeholder="1" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Buy Unit</label>
              <select data-testid="brand-offer-buy-unit" value={form.offer_buy_unit} onChange={(e) => set("offer_buy_unit", e.target.value)} className={inputCls}>
                {OFFER_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            {useCash && (
              <div className="col-span-2">
                <label className={labelCls}>Cash Discount (₹)</label>
                <input data-testid="brand-offer-cash" type="number" step="0.01" inputMode="decimal" value={form.offer_cash_discount} onChange={(e) => set("offer_cash_discount", e.target.value)} placeholder="10" className={inputCls} />
              </div>
            )}
          </div>

          {useFree && (
            <div className="rounded-lg border border-indigo-100 bg-white p-3" data-testid="brand-offer-free-fields">
              <p className="mb-2 text-xs font-semibold text-indigo-700">Free Item</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <div>
                  <label className={labelCls}>Free Qty</label>
                  <input data-testid="brand-offer-free-qty" type="number" inputMode="numeric" value={form.offer_free_qty} onChange={(e) => set("offer_free_qty", e.target.value)} placeholder="2" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Free Unit</label>
                  <select data-testid="brand-offer-free-unit" value={form.offer_free_unit} onChange={(e) => set("offer_free_unit", e.target.value)} className={inputCls}>
                    {OFFER_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className={labelCls}>Free Item Name</label>
                  <input data-testid="brand-offer-free-item" value={form.offer_free_item} onChange={(e) => set("offer_free_item", e.target.value)} placeholder="Agarbathi, T-Shirt, Gift..." className={inputCls} />
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Offer Start Date</label>
              <input data-testid="brand-offer-start-date" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Offer End Date</label>
              <input data-testid="brand-offer-end-date" type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Offer Description</label>
            <input data-testid="brand-offer-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g. Festive Brand Special" className={inputCls} />
          </div>

          <div className="flex gap-2">
            <button data-testid="save-brand-offer" type="submit" disabled={saving} className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {editingId ? "Update Brand Offer" : "Save Brand Offer"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50">
              <X className="h-4 w-4" /> Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
      ) : offers.length === 0 ? (
        <p className="py-12 text-center text-sm text-slate-400">No brand offers yet. Create one to apply an offer to every product of a brand.</p>
      ) : (
        <div className="space-y-2">
          {offers.map((o) => (
            <div key={o.id} data-testid={`brand-offer-${o.id}`} className="rounded-lg border border-indigo-100 bg-white p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                    <Sparkles className="h-3.5 w-3.5 text-indigo-500" /> {o.brand}
                  </p>
                  <p className="mt-0.5 text-xs font-medium text-indigo-700" data-testid={`brand-offer-text-${o.id}`}>{brandOfferText(o)}</p>
                  {brandOfferValidityText(o) && (
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      {brandOfferValidityText(o)}{" · "}
                      <span className={o.active ? "font-semibold text-green-600" : "font-semibold text-slate-400"}>
                        {o.active ? "Live now" : "Not active (scheduled/expired)"}
                      </span>
                    </p>
                  )}
                  {o.description && <p className="mt-0.5 text-[11px] text-slate-400">{o.description}</p>}
                </div>
                <div className="flex gap-2">
                  <button data-testid={`edit-brand-offer-${o.id}`} onClick={() => openEdit(o)} className="flex items-center gap-1 rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button data-testid={`remove-brand-offer-${o.id}`} onClick={() => remove(o.id)} className="flex items-center gap-1 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100">
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
